/**
 * OutcomePanel: "Rep's actual reply", what the rep really answered.
 *
 * Shadow-mode evidence. The mail ingest looks the enquiry up in the base mailbox
 * (same `internetMessageId`) and finds the rep's earliest reply in Sent Items. This
 * panel renders that stored outcome (`QuoteCaseDetail.outcome`): when the rep replied
 * and after how many hours, the rep's own text, and the document attached to the
 * reply. When that is a quotation or an order confirmation, its lines are shown as
 * quoted (item, description, quantity, sales price) together with one verdict per
 * draft line: same item, a different item, or not in the document.
 * `reply_found: false` renders "No reply found yet".
 *
 * Read-only display; used by the case review and by the Outlook add-in pane.
 */

import { Icon, Reply } from "@norican/icons";
import {
  Badge,
  Button,
  Card,
  cn,
  DataGrid,
  type DataGridColumnDef,
  DescriptionList,
  EmptyState,
  Typography,
} from "@norican/react";
import { useMemo, useState } from "react";

import { type OutcomeDocLine, type OutcomeDocType, type OutcomeLineVerdict } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { type CaseOutcome } from "@/lib/mock";
import { TYPE3_SENTENCE, wonValueText } from "@/lib/work-labels";

/** Reply texts longer than this start collapsed. */
const COLLAPSE_LINES = 12;
const COLLAPSE_CHARS = 900;

/** ISO → local date + time; empty/invalid → "unknown". */
function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "unknown";
  }
  return formatDateTime(value);
}

/** Hours → "under 1 h" / "5 h" / "3 d 4 h". */
function formatHours(hours: number): string {
  if (hours < 1) {
    return "under 1 h";
  }
  const rounded = Math.round(hours);
  if (rounded < 48) {
    return `${rounded} h`;
  }
  const days = Math.floor(rounded / 24);
  const rest = rounded % 24;
  return rest === 0 ? `${days} d` : `${days} d ${rest} h`;
}

/** The rep's own text, pre-wrapped, collapsed after about twelve lines. */
function ReplyText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const long = text.split("\n").length > COLLAPSE_LINES || text.length > COLLAPSE_CHARS;
  return (
    <div className="flex flex-col items-start gap-1.5">
      <div
        className={cn(
          "bg-bg-inset text-fg w-full rounded-md px-3 py-2 text-[0.8125rem] leading-relaxed break-words whitespace-pre-wrap",
          long && !expanded && "line-clamp-[12]",
        )}
      >
        {text}
      </div>
      {long && (
        <Button
          variant="ghost"
          tone="neutral"
          size="sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Show less" : "Show the full reply"}
        </Button>
      )}
    </div>
  );
}

/** Wording per document kind: the heading and the noun used in the verdicts. */
const DOC_WORDING: Record<OutcomeDocType, { heading: string; verb: string; noun: string }> = {
  quotation: { heading: "Quotation sent", verb: "quoted", noun: "the quotation" },
  order_confirmation: {
    heading: "Order confirmation sent",
    verb: "confirmed",
    noun: "the order confirmation",
  },
  proforma: { heading: "Proforma invoice sent", verb: "invoiced", noun: "the proforma invoice" },
  other: { heading: "Document sent", verb: "listed", noun: "the document" },
};

/** The status of a quotation in D365 as shown on the badge. */
const QUOTE_STATUS_LABEL: Record<string, string> = {
  created: "created, not sent yet",
  sent: "sent",
  confirmed: "confirmed",
  lost: "lost",
  cancelled: "cancelled",
  unknown: "not known",
};

/** "2 internal mails before this reply were skipped", or null when none were. */
function internalSkippedText(count: number | null | undefined): string | null {
  if (!count || count <= 0) {
    return null;
  }
  return count === 1
    ? "I2Q skipped 1 internal mail before this reply"
    : `I2Q skipped ${count} internal mails before this reply`;
}

/** A quantity as printed: whole numbers without decimals. */
function formatQty(value: number | null | undefined): string {
  if (value == null) {
    return "";
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

/** A sales price or amount with two decimals. */
function formatMoney(value: number | null | undefined): string {
  if (value == null) {
    return "";
  }
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** The lines of the sent document, as quoted to the customer. */
function QuotedLines({
  lines,
  currency,
  compact,
}: {
  lines: OutcomeDocLine[];
  currency: string | null;
  compact: boolean;
}) {
  const columns = useMemo<DataGridColumnDef<OutcomeDocLine>[]>(() => {
    const unit = currency ? ` (${currency})` : "";
    const all: DataGridColumnDef<OutcomeDocLine>[] = [
      {
        id: "item",
        header: "Item",
        accessor: (r) => r.item,
        cell: (r) => <span className="text-fg font-mono">{r.item}</span>,
      },
      { id: "description", header: "Description", accessor: (r) => r.description },
      {
        id: "qty",
        header: "Qty",
        accessor: (r) => r.qty ?? 0,
        numeric: true,
        align: "end",
        cell: (r) => [formatQty(r.qty), r.unit].filter(Boolean).join(" "),
      },
      {
        id: "unit_price",
        header: `Unit price${unit}`,
        accessor: (r) => r.unit_price ?? 0,
        numeric: true,
        align: "end",
        cell: (r) => formatMoney(r.unit_price),
      },
      {
        id: "amount",
        header: `Amount${unit}`,
        accessor: (r) => r.amount ?? 0,
        numeric: true,
        align: "end",
        cell: (r) => formatMoney(r.amount),
      },
    ];
    return compact ? all.filter((c) => c.id !== "description" && c.id !== "amount") : all;
  }, [currency, compact]);

  return (
    <div className="overflow-x-auto">
      <DataGrid
        columns={columns}
        data={lines}
        getRowId={(r) => `${lines.indexOf(r)}-${r.item}`}
        density="compact"
        aria-label="Lines of the sent document"
      />
    </div>
  );
}

/** One draft line with its verdict tag. */
function VerdictRow({
  verdict,
  docType,
}: {
  verdict: OutcomeLineVerdict;
  docType: OutcomeDocType;
}) {
  const { verb, noun } = DOC_WORDING[docType];
  let tone: "success" | "warning" | "neutral" = "neutral";
  let text = `Not in ${noun}`;
  if (verdict.verdict === "same_item") {
    tone = "success";
    text = `Same item ${verb}`;
  } else if (verdict.verdict === "different_item") {
    tone = "warning";
    text = `Sales ${verb} a different item: ${verdict.quoted_item ?? "unknown"}`;
  } else if (verdict.verdict === "no_draft_item") {
    text = verdict.quoted_item
      ? `No item proposed. Sales ${verb}: ${verdict.quoted_item}`
      : "No item proposed";
  }
  const qtyDiffers =
    verdict.verdict === "same_item" &&
    verdict.qty_match === false &&
    verdict.draft_qty != null &&
    verdict.quoted_qty != null;
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Typography variant="body-sm" tone="muted">
        Line {verdict.draft_index + 1}
      </Typography>
      {verdict.draft_item && (
        <span className="text-fg font-mono text-[0.8125rem]">{verdict.draft_item}</span>
      )}
      <Badge tone={tone} variant="subtle" size="sm">
        {text}
      </Badge>
      {qtyDiffers && (
        <Typography variant="caption" tone="subtle">
          Quantity differs: draft {formatQty(verdict.draft_qty)}, {verb}{" "}
          {formatQty(verdict.quoted_qty)}
        </Typography>
      )}
    </li>
  );
}

/** The document attached to the reply: header, quoted lines, verdict per draft line. */
function SentDocument({
  outcome,
  compact,
  caseAccount,
}: {
  outcome: CaseOutcome;
  compact: boolean;
  caseAccount: string | null;
}) {
  const docCustomer = outcome.doc_customer_number?.trim() || null;
  const otherCustomer = Boolean(docCustomer && caseAccount && docCustomer !== caseAccount.trim());
  const docType: OutcomeDocType = outcome.doc_type ?? "other";
  const wording = DOC_WORDING[docType];
  const lines = outcome.doc_lines ?? [];
  const comparison = outcome.comparison ?? [];
  const extra = outcome.extra_quoted ?? [];
  const agreement = outcome.line_agreement;
  const matched = agreement == null ? null : Math.round(agreement * lines.length);

  return (
    <div className="border-border-subtle flex flex-col gap-3 border-t pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="label">
          {wording.heading}: <span className="font-mono">{outcome.doc_number}</span>
        </Typography>
        {matched != null && (
          <Badge tone={matched === lines.length ? "success" : "neutral"} variant="subtle" size="sm">
            {matched} of {lines.length} quoted {lines.length === 1 ? "line" : "lines"} in our draft
          </Badge>
        )}
        {outcome.quote_status && (
          <Badge tone="neutral" variant="outline" size="sm">
            Status in F&amp;O: {QUOTE_STATUS_LABEL[outcome.quote_status] ?? outcome.quote_status}
          </Badge>
        )}
        {!outcome.quote_status && outcome.doc_type === "order_confirmation" && (
          <Badge tone="neutral" variant="outline" size="sm">
            No quotation found for this sales order
          </Badge>
        )}
        {outcome.sales_order && (
          <Badge tone="success" variant="subtle" size="sm">
            Sales order {outcome.sales_order}
          </Badge>
        )}
      </div>
      {outcome.won_amount != null && (
        <Typography variant="caption" tone="subtle">
          Won value: {wonValueText(outcome.won_amount, outcome.doc_currency)}
        </Typography>
      )}
      {docCustomer && (
        <Typography variant="caption" tone="muted" className="break-words">
          Customer number on the sent document: <span className="font-mono">{docCustomer}</span>
          {otherCustomer ? `. The customer of this case is ${caseAccount?.trim()}.` : ""}
        </Typography>
      )}
      {outcome.doc_method === "type3" && (
        <Typography variant="caption" tone="subtle">
          {TYPE3_SENTENCE}
        </Typography>
      )}
      {lines.length > 0 ? (
        <QuotedLines lines={lines} currency={outcome.doc_currency ?? null} compact={compact} />
      ) : (
        <Typography variant="body-sm" tone="muted">
          {outcome.doc_note ?? "I2Q could not read the lines of this document."}
        </Typography>
      )}
      {comparison.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Typography variant="caption" tone="subtle">
            Our draft compared with {wording.noun}
          </Typography>
          <ul className="flex flex-col gap-1.5">
            {comparison.map((verdict) => (
              <VerdictRow key={verdict.draft_index} verdict={verdict} docType={docType} />
            ))}
          </ul>
        </div>
      )}
      {extra.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Typography variant="caption" tone="subtle">
            Also {wording.verb}, not in our draft:
          </Typography>
          {extra.map((line, index) => (
            <Badge
              key={`${index}-${line.item}`}
              tone="neutral"
              variant="subtle"
              size="xs"
              className="font-mono"
            >
              {line.item}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export interface OutcomePanelProps {
  outcome: CaseOutcome;
  /** Narrow layout for the Outlook task pane. */
  compact?: boolean;
  className?: string;
  /** The customer account of the case, to say when the sent document names another one. */
  caseAccount?: string | null;
}

export function OutcomePanel({
  outcome,
  compact = false,
  className,
  caseAccount = null,
}: OutcomePanelProps) {
  const header = (
    <Card.Header className="border-border-subtle flex items-center gap-2 border-b" padding="sm">
      <Icon icon={Reply} size="sm" className="text-fg-subtle" />
      <Typography variant="label">Sales reply</Typography>
      <Typography variant="caption" tone="subtle" className="ml-auto">
        checked {formatTimestamp(outcome.checked_at)}
      </Typography>
    </Card.Header>
  );

  const skipped = internalSkippedText(outcome.internal_skipped);

  if (!outcome.reply_found) {
    return (
      <Card variant="outline" padding="none" className={className}>
        {header}
        <Card.Body>
          <EmptyState
            size="sm"
            icon={<Icon icon={Reply} size="md" className="text-fg-subtle" />}
            title={
              skipped ? "Forwarded internally, no reply to the customer yet" : "No reply found yet"
            }
            description={
              skipped
                ? "The mail went to a colleague. The Sent Items of the shared mailbox hold no reply to the customer yet. I2Q checks again every 6 hours for 30 days."
                : "The Sent Items of the shared mailbox hold no reply to this mail yet. I2Q checks again every 6 hours for 30 days. I2Q does not see replies from a personal mailbox."
            }
          />
        </Card.Body>
      </Card>
    );
  }

  const replyText = outcome.reply_text?.trim() ?? "";
  const hasDocument = Boolean(outcome.doc_number) || (outcome.doc_lines ?? []).length > 0;
  // Codes found in the mail text are weak evidence; the document replaces them.
  const matched = hasDocument ? [] : (outcome.matched_items ?? []);
  const items = [
    { label: "Replied", value: formatTimestamp(outcome.replied_at) },
    ...(outcome.reply_mailbox ? [{ label: "Mailbox", value: outcome.reply_mailbox }] : []),
    ...(matched.length > 0
      ? [
          {
            label: `Items also in the I2Q draft (${matched.length})`,
            value: (
              <span className="flex flex-wrap gap-1">
                {matched.map((code) => (
                  <Badge key={code} tone="success" variant="subtle" size="xs" className="font-mono">
                    {code}
                  </Badge>
                ))}
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card variant="outline" padding="none" className={className}>
      {header}
      <Card.Body className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="success" variant="subtle">
            {outcome.reply_hours == null
              ? "Sales replied"
              : `Sales replied after ${formatHours(outcome.reply_hours)}`}
          </Badge>
          {outcome.reply_to === "external" && (
            <Typography variant="caption" tone="subtle">
              Sent to an external address other than the sender
            </Typography>
          )}
          {skipped && (
            <Typography variant="caption" tone="subtle">
              {skipped}
            </Typography>
          )}
        </div>
        {replyText ? (
          <ReplyText text={replyText} />
        ) : (
          <Typography variant="body-sm" tone="muted">
            The reply has no text of its own. It may consist of an attachment only.
          </Typography>
        )}
        <DescriptionList orientation={compact ? "vertical" : "horizontal"} items={items} />
        {hasDocument ? (
          <SentDocument outcome={outcome} compact={compact} caseAccount={caseAccount} />
        ) : (
          <Typography variant="caption" tone="subtle">
            {outcome.doc_checked_at
              ? (outcome.doc_note ?? "The reply carries no quotation and no order confirmation.")
              : "I2Q has not read the attachments of this reply yet. It reads them within the next hours."}
          </Typography>
        )}
      </Card.Body>
    </Card>
  );
}
